/**
 * GlassAlert — drop-in replacement for sweetalert2 (`Alert.fire`).
 *
 * Vanilla-DOM (no React) so it works from anywhere the old `Swal`/`Alert`
 * was called, including Redux reducers and plain event handlers. Renders a
 * v4 Liquid Glass modal and returns a Promise resolving to a SweetAlert2-
 * compatible result: { isConfirmed, isDenied, isDismissed, value }.
 *
 * Supported option surface (the subset this codebase uses):
 *   title, text, html, icon ('success'|'error'|'warning'|'info'|'question'),
 *   showCancelButton, showDenyButton,
 *   confirmButtonText, denyButtonText, cancelButtonText,
 *   confirmButtonColor, denyButtonColor, cancelButtonColor
 *
 * Positional form is also supported: fire('title', 'text', 'icon').
 */

let stylesInjected = false;

function injectStyles() {
  if (stylesInjected) return;
  stylesInjected = true;
  const css = `
.glass-alert-overlay {
  position: fixed; inset: 0; z-index: 20000;
  display: flex; align-items: center; justify-content: center;
  background: rgba(15, 31, 46, 0.38);
  -webkit-backdrop-filter: blur(6px); backdrop-filter: blur(6px);
  opacity: 0; transition: opacity .18s ease;
  padding: 20px;
}
.glass-alert-overlay.is-open { opacity: 1; }
.glass-alert {
  width: 100%; max-width: 420px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(255, 255, 255, 0.7);
  border-radius: 18px;
  box-shadow: 0 24px 60px rgba(15, 31, 46, 0.28);
  padding: 28px 26px 22px;
  text-align: center;
  font-family: var(--font-body, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif);
  color: var(--text, #0f1f2e);
  transform: translateY(8px) scale(0.98); transition: transform .18s ease;
}
.glass-alert-overlay.is-open .glass-alert { transform: translateY(0) scale(1); }
.glass-alert-icon {
  width: 56px; height: 56px; margin: 0 auto 14px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 28px; color: #fff;
}
.glass-alert-icon.success { background: linear-gradient(135deg, #21b573, #14904f); }
.glass-alert-icon.error   { background: linear-gradient(135deg, #e84651, #c0303a); }
.glass-alert-icon.warning { background: linear-gradient(135deg, #f5a623, #db8a00); }
.glass-alert-icon.info    { background: linear-gradient(135deg, #2563eb, #1d4fd8); }
.glass-alert-icon.question{ background: linear-gradient(135deg, #7c5cfc, #5a3fd6); }
.glass-alert-title { font-size: 1.18rem; font-weight: 800; letter-spacing: -0.01em; margin: 0 0 6px; }
.glass-alert-text { font-size: 0.95rem; color: var(--text-muted, #5a6b7b); margin: 0 0 18px; line-height: 1.45; }
.glass-alert-text:empty { display: none; margin: 0; }
.glass-alert-actions { display: flex; gap: 10px; justify-content: center; flex-wrap: wrap; }
.glass-alert-btn {
  border: none; cursor: pointer; border-radius: 12px;
  padding: 10px 22px; font-size: 0.95rem; font-weight: 700;
  font-family: inherit; color: #fff; min-width: 92px;
  transition: transform .12s ease, box-shadow .15s ease, opacity .15s ease;
}
.glass-alert-btn:hover { transform: translateY(-1px); }
.glass-alert-btn.confirm { background: linear-gradient(135deg, var(--primary, #0DA1B2), var(--primary-dark, #067886)); box-shadow: 0 8px 20px rgba(13, 161, 178, 0.32); }
.glass-alert-btn.deny    { background: linear-gradient(135deg, #e84651, #c0303a); box-shadow: 0 8px 20px rgba(232, 70, 81, 0.28); }
.glass-alert-btn.cancel  { background: #eef2f5; color: #5a6b7b; }
`;
  const style = document.createElement('style');
  style.setAttribute('data-glass-alert', '');
  style.appendChild(document.createTextNode(css));
  document.head.appendChild(style);
}

const ICONS = { success: '✓', error: '✕', warning: '!', info: 'i', question: '?' };

function fire(arg1, arg2, arg3) {
  let opts;
  if (typeof arg1 === 'string' || arg1 == null) {
    opts = { title: arg1 || '', text: arg2, icon: arg3 };
  } else {
    opts = arg1 || {};
  }

  injectStyles();

  return new Promise((resolve) => {
    const overlay = document.createElement('div');
    overlay.className = 'glass-alert-overlay';

    const card = document.createElement('div');
    card.className = 'glass-alert';

    // Icon
    if (opts.icon && ICONS[opts.icon]) {
      const iconEl = document.createElement('div');
      iconEl.className = 'glass-alert-icon ' + opts.icon;
      iconEl.textContent = ICONS[opts.icon];
      card.appendChild(iconEl);
    }

    // Title
    if (opts.title) {
      const titleEl = document.createElement('div');
      titleEl.className = 'glass-alert-title';
      titleEl.textContent = opts.title;
      card.appendChild(titleEl);
    }

    // Body — html takes precedence over text
    if (opts.html != null) {
      const bodyEl = document.createElement('div');
      bodyEl.className = 'glass-alert-text';
      bodyEl.innerHTML = opts.html;
      card.appendChild(bodyEl);
    } else if (opts.text != null) {
      const bodyEl = document.createElement('div');
      bodyEl.className = 'glass-alert-text';
      bodyEl.textContent = opts.text;
      card.appendChild(bodyEl);
    }

    const actions = document.createElement('div');
    actions.className = 'glass-alert-actions';

    let settled = false;
    const close = (result) => {
      if (settled) return;
      settled = true;
      overlay.classList.remove('is-open');
      document.removeEventListener('keydown', onKey);
      setTimeout(() => {
        if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
        resolve(result);
      }, 180);
    };

    // Confirm (always shown — matches SweetAlert2 default)
    const confirmBtn = document.createElement('button');
    confirmBtn.type = 'button';
    confirmBtn.className = 'glass-alert-btn confirm';
    confirmBtn.textContent = opts.confirmButtonText || 'OK';
    if (opts.confirmButtonColor) confirmBtn.style.background = opts.confirmButtonColor;
    confirmBtn.addEventListener('click', () => close({ isConfirmed: true, isDenied: false, isDismissed: false, value: true }));
    actions.appendChild(confirmBtn);

    // Deny
    if (opts.showDenyButton) {
      const denyBtn = document.createElement('button');
      denyBtn.type = 'button';
      denyBtn.className = 'glass-alert-btn deny';
      denyBtn.textContent = opts.denyButtonText || 'No';
      if (opts.denyButtonColor) denyBtn.style.background = opts.denyButtonColor;
      denyBtn.addEventListener('click', () => close({ isConfirmed: false, isDenied: true, isDismissed: false, value: false }));
      actions.appendChild(denyBtn);
    }

    // Cancel
    if (opts.showCancelButton) {
      const cancelBtn = document.createElement('button');
      cancelBtn.type = 'button';
      cancelBtn.className = 'glass-alert-btn cancel';
      cancelBtn.textContent = opts.cancelButtonText || 'Cancel';
      if (opts.cancelButtonColor) cancelBtn.style.background = opts.cancelButtonColor;
      cancelBtn.addEventListener('click', () => close({ isConfirmed: false, isDenied: false, isDismissed: true, dismiss: 'cancel' }));
      actions.appendChild(cancelBtn);
    }

    card.appendChild(actions);
    overlay.appendChild(card);

    // Scrim click dismisses
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) close({ isConfirmed: false, isDenied: false, isDismissed: true, dismiss: 'backdrop' });
    });

    // Keyboard: Enter confirms, Esc dismisses
    const onKey = (e) => {
      if (e.key === 'Enter') { e.preventDefault(); confirmBtn.click(); }
      else if (e.key === 'Escape') { e.preventDefault(); close({ isConfirmed: false, isDenied: false, isDismissed: true, dismiss: 'esc' }); }
    };
    document.addEventListener('keydown', onKey);

    document.body.appendChild(overlay);
    // Trigger open transition
    requestAnimationFrame(() => {
      overlay.classList.add('is-open');
      confirmBtn.focus();
    });
  });
}

const GlassAlert = { fire };

export default GlassAlert;
